<template>
    <div>
        <v-card :loading="loading">
            <v-card-title class="text-h5">Generate Report</v-card-title>
            <v-card-text>
                <div>
                    <!-- Report Type -->
                    <v-select
                        v-model="reportType"
                        :items="reportTypes"
                        label="Select the type of report."
                        :rules="[rules.required]"
                    ></v-select>
                    <!-- General Report -->
                    <v-select
                        v-if="reportType == 'By Status'"
                        v-model="selectedStatus"
                        :items="woStatus"
                        label="Select the work orders status to generate the report."
                        multiple
                        :rules="[rules.required]"
                    ></v-select>
                    <!-- Per Process Report -->
                    <v-select
                        v-if="
                            reportType == 'Per Process' ||
                                reportType ==
                                    'Entered, Produced and Accumulated Per Process'
                        "
                        v-model="selectedProcess"
                        :items="enabledProcesses"
                        item-text="name"
                        item-value="id"
                        label="Select the process to generate the report."
                        :rules="[rules.required]"
                    />
                </div>
                <!-- Specific process in month. -->
                <div
                    v-if="
                        reportType ==
                            'Entered, Produced and Accumulated Per Process'
                    "
                >
                    <v-row>
                        <v-col cols="12">
                            <v-menu
                                v-model="menuSelectedDay"
                                transition="scale-transition"
                                offset-y
                                content-class="wide-date-menu"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="selectedDay"
                                        label="Day"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        clearable
                                        :rules="[rules.required]"
                                    />
                                </template>
                                <v-date-picker
                                    :key="`day-picker-${reportType}`"
                                    v-model="selectedDay"
                                    :min="minEndDate"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                </div>
                <!-- All processes in month. -->
                <div
                    v-else-if="
                        reportType ==
                            'Entered, Produced and Accumulated All Processes'
                    "
                >
                    <v-row>
                        <v-col cols="12">
                            <v-menu
                                v-model="menuSelectedMonth"
                                transition="scale-transition"
                                offset-y
                                content-class="wide-date-menu"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="selectedMonth"
                                        label="Month"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        clearable
                                        :rules="[rules.required]"
                                    />
                                </template>

                                <v-date-picker
                                    :key="`month-picker-${reportType}`"
                                    v-model="selectedMonth"
                                    :min="minEndDate"
                                    type="month"
                                    scrollable
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                </div>
                <!-- Monthly range for Shipped Per Month. -->
                <div
                    v-else-if="reportType == 'Shipped Per Month'"
                    class="my-5 mb-n4"
                >
                    <v-row>
                        <v-col cols="12">
                            <v-menu
                                v-model="menuMonthlyRange"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                content-class="wide-date-menu"
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="monthlyRangeText"
                                        label="Monthly Range"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        clearable
                                        @click:clear="monthlyRangeDates = []"
                                        :rules="[
                                            rules.required,
                                            validateMaxMonthlyRange,
                                        ]"
                                    />
                                </template>
                                <v-date-picker
                                    :key="`monthly-picker-${reportType}`"
                                    v-model="monthlyRangeDates"
                                    range
                                    type="month"
                                    scrollable
                                    :max="maxMonthlyRange"
                                    @input="onMonthlyRangeInput"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                </div>
                <!-- General start and end date. -->
                <div class="my-5 mb-n4" v-else>
                    <v-row>
                        <v-col cols="12">
                            <v-menu
                                v-model="menuDateRange"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                content-class="wide-date-menu"
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="dateRangeText"
                                        label="Date Range"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        clearable
                                        @click:clear="dates = []"
                                        :rules="[
                                            rules.required,
                                            validateMaxRange,
                                        ]"
                                    />
                                </template>
                                <v-date-picker
                                    :key="`date-picker-${reportType}`"
                                    v-model="dates"
                                    range
                                    scrollable
                                    :max="
                                        new Date().toISOString().substr(0, 10)
                                    "
                                    @input="onDateRangeInput"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                </div>
                <div v-if="reportDescriptions[reportType]">
                    <v-row>
                        <v-col cols="1">
                            <v-icon>mdi-information-outline</v-icon>
                        </v-col>
                        <v-col cols="11">
                            <p class="my-0">
                                {{ reportDescriptions[reportType] }}
                            </p>
                        </v-col>
                    </v-row>
                </div>
            </v-card-text>
            <v-card-text v-if="downloadUrl" class="pt-0">
                <v-alert type="success" dense outlined>
                    <div class="text-subtitle-2 mb-2">Report ready</div>
                    <div class="text-caption mb-3" v-if="downloadExpiresAt">
                        Link expires: {{ downloadExpiresAt }}
                    </div>
                </v-alert>
                <div class="d-flex flex-wrap" style="gap: 8px;">
                    <v-btn
                        color="primary"
                        :href="downloadUrl"
                        target="_blank"
                        rel="noopener"
                        download
                        style="font-size: 10px;"
                    >
                        <v-icon left>mdi-download</v-icon>
                        Download {{ downloadFileName || 'report.xlsx' }}
                    </v-btn>
                    <v-btn
                        color="secondary"
                        outlined
                        style="font-size: 10px;"
                        @click="copyDownloadLink"
                    >
                        <v-icon left>{{
                            linkCopied ? 'mdi-check' : 'mdi-content-copy'
                        }}</v-icon>
                        {{ linkCopied ? 'Link copied!' : 'Copy link' }}
                    </v-btn>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    text
                    color="secondary"
                    :disabled="loading"
                    @click="closeWoReport"
                >
                    Close
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    color="primary"
                    :disabled="loading || !isValid"
                    @click="generateReport()"
                >
                    Generate
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import moment from 'moment'
import { generateWorkOrderReport } from '@/services/reportsApi'

export default {
    name: 'WorkOrderReport',
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
        projects: {
            type: Object,
            default: () => ({}),
        },
        clientNames: {
            type: Object,
            default: () => ({}),
        },
        userNames: {
            type: Object,
            default: () => ({}),
        },
        processes: {
            type: Array,
            default: () => [],
        },
        processesNames: {
            type: Object,
            default: () => ({}),
        },
        processesData: {
            type: Object,
            default: () => ({}),
        },
        finishAndUCCodes: {
            type: Object,
            default: () => ({}),
        },
        productTypes: {
            type: Object,
            default: () => ({}),
        },
        products: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        loading: false,
        downloadUrl: null,
        downloadFileName: null,
        downloadExpiresAt: null,
        linkCopied: false,
        reportType: undefined,
        reportTypes: [
            'By Status',
            'Produced KG Per Process',
            'Per Product Type',
            'Per Product',
            'Shipped Per Month',
            'Per Process',
            'Entered, Produced and Accumulated Per Process',
            'Entered, Produced and Accumulated All Processes',
            'Rejection',
            'Rejected Work Orders',
            'History of Generated WO',
        ],
        selectedStatus: [],
        woStatus: [
            'inProgress',
            'sent',
            'approved',
            'finished',
            'closed',
            'canceled',
            'deleted',
        ],
        rules: {
            required: v => !!v || 'Required',
        },
        readyForPackingProcess: {
            id: 'readyForPacking',
            name: 'Ready for packing',
        },
        packingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        qualityProcess: {
            id: 'quality',
            name: 'quality',
        },
        dates: [],
        menuDateRange: false,
        selectedDay: undefined,
        menuSelectedDay: false,
        selectedMonth: undefined,
        menuSelectedMonth: false,
        monthlyRangeDates: [],
        menuMonthlyRange: false,
        selectedProcess: undefined,
        reportDescriptions: {
            'By Status':
                'Lists work orders that currently have the selected status(es) and were created within the selected dates. Maximum date range: 3 months.',
            'Produced KG Per Process':
                'Generate a report by produced kg per process. Maximum date range: 3 months.',
            'Per Product Type':
                'Generate a report by product type. Maximum date range: 3 months.',
            'Per Product':
                'Generate a report by material type. Maximum date range: 3 months.',
            'Shipped Per Month':
                'Generate a report by shipped per month. Maximum monthly range: 3 months.',
            'Per Process':
                'Generate a report by process. Maximum date range: 60 days.',
            'Entered, Produced and Accumulated Per Process':
                'Generate a report by entered, produced and accumulated per process.',
            'Entered, Produced and Accumulated All Processes':
                'Generate a report by entered, produced and accumulated all processes.',
            Rejection:
                'Rejection counts by designer for the selected dates (created WOs and rejection events in range). Maximum date range: 3 months.',
            'Rejected Work Orders':
                'Detail of rejection events in the selected dates. Maximum date range: 3 months.',
            'History of Generated WO':
                'Work orders created (generated) within the selected dates. Maximum date range: 3 months.',
        },
    }),
    watch: {
        reportType(newValue, oldValue) {
            // Clear form fields when report type changes
            if (oldValue !== undefined && newValue !== oldValue) {
                // Close any open menus first to prevent rendering issues
                this.menuDateRange = false
                this.menuMonthlyRange = false
                this.menuSelectedDay = false
                this.menuSelectedMonth = false

                // Clear form fields after menu closes to ensure proper cleanup
                this.$nextTick(() => {
                    // Clear date ranges - reset to empty arrays
                    this.dates = []
                    this.monthlyRangeDates = []

                    // Clear other selection fields
                    this.selectedStatus = []
                    this.selectedProcess = undefined
                    this.selectedDay = undefined
                    this.selectedMonth = undefined
                    this.downloadUrl = null
                    this.downloadFileName = null
                    this.downloadExpiresAt = null
                    this.linkCopied = false
                })
            }
        },
    },
    computed: {
        enabledProcesses() {
            return (this.processes || []).filter(p => p.enabled !== false)
        },
        dateRangeText() {
            if (!this.dates || this.dates.length === 0) return ''
            if (this.dates.length === 1) return this.dates[0]
            return this.dates.join(' ~ ')
        },
        monthlyRangeText() {
            if (!this.monthlyRangeDates || this.monthlyRangeDates.length === 0)
                return ''
            if (this.monthlyRangeDates.length === 1)
                return this.monthlyRangeDates[0]
            return this.monthlyRangeDates.join(' ~ ')
        },
        startDate() {
            // For Shipped Per Month, use monthly range in YYYY-MM format
            if (
                this.reportType === 'Shipped Per Month' &&
                this.monthlyRangeDates &&
                this.monthlyRangeDates.length > 0
            ) {
                const months = [...this.monthlyRangeDates].sort()
                return months[0]
            }
            if (this.dates && this.dates.length > 0) {
                if (this.dates.length === 2) {
                    return this.dates[0] <= this.dates[1]
                        ? this.dates[0]
                        : this.dates[1]
                }
                return this.dates[0]
            }
            return null
        },
        endDate() {
            // For Shipped Per Month, use monthly range in YYYY-MM format
            if (
                this.reportType === 'Shipped Per Month' &&
                this.monthlyRangeDates &&
                this.monthlyRangeDates.length === 2
            ) {
                const months = [...this.monthlyRangeDates].sort()
                return months[1]
            }
            if (this.dates && this.dates.length === 2) {
                return this.dates[0] <= this.dates[1]
                    ? this.dates[1]
                    : this.dates[0]
            }
            return null
        },
        minEndDate() {
            return this.dates && this.dates.length > 0 ? this.dates[0] : null
        },
        maxMonthlyRange() {
            if (
                !this.monthlyRangeDates ||
                this.monthlyRangeDates.length === 0
            ) {
                return new Date().toISOString().substr(0, 7)
            }
            const startMonth = this.monthlyRangeDates[0]
            const startDate = new Date(startMonth + '-01')
            const max = new Date(startDate)
            max.setMonth(max.getMonth() + 3)
            const today = new Date()
            return (max > today ? today : max).toISOString().substr(0, 7)
        },
        isValid() {
            const startDateAndEndDateCondition =
                this.dates &&
                this.dates.length === 2 &&
                this.dates[0] &&
                this.dates[1]
            const monthlyRangeCondition =
                this.monthlyRangeDates &&
                this.monthlyRangeDates.length === 2 &&
                this.monthlyRangeDates[0] &&
                this.monthlyRangeDates[1]
            switch (this.reportType) {
                case 'By Status':
                    return (
                        this.selectedStatus.length > 0 &&
                        startDateAndEndDateCondition &&
                        this.isDateRangeWithinLimit()
                    )
                case 'Per Process':
                    return (
                        this.selectedProcess != undefined &&
                        startDateAndEndDateCondition &&
                        this.isDateRangeWithinLimit()
                    )

                case 'Entered, Produced and Accumulated Per Process':
                    return (
                        this.selectedProcess != undefined &&
                        this.selectedDay != undefined
                    )
                case 'Entered, Produced and Accumulated All Processes':
                    return this.selectedMonth != undefined
                case 'Produced KG Per Process':
                    return (
                        startDateAndEndDateCondition &&
                        this.isDateRangeWithinLimit()
                    )
                case 'Per Product Type':
                    return (
                        startDateAndEndDateCondition &&
                        this.isDateRangeWithinLimit()
                    )
                case 'Per Product':
                    return (
                        startDateAndEndDateCondition &&
                        this.isDateRangeWithinLimit()
                    )
                case 'Shipped Per Month':
                    return (
                        monthlyRangeCondition &&
                        this.validateMaxMonthlyRange() === true
                    )
                case 'Rejection':
                    return (
                        startDateAndEndDateCondition &&
                        this.isDateRangeWithinLimit()
                    )
                case 'Rejected Work Orders':
                    return (
                        startDateAndEndDateCondition &&
                        this.isDateRangeWithinLimit()
                    )
                case 'History of Generated WO':
                    return (
                        startDateAndEndDateCondition &&
                        this.isDateRangeWithinLimit()
                    )
                default:
                    return false
            }
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        // Report generation via Cloud Function
        buildReportPayload() {
            switch (this.reportType) {
                case 'By Status':
                    return {
                        startDate: this.startDate,
                        endDate: this.endDate,
                        status: this.selectedStatus,
                    }
                case 'Shipped Per Month':
                    return {
                        startDate: this.startDate,
                        endDate: this.endDate,
                    }
                case 'Per Process':
                    return {
                        startDate: this.startDate,
                        endDate: this.endDate,
                        processId: this.selectedProcess,
                    }
                case 'Entered, Produced and Accumulated Per Process':
                    return {
                        reportDate: this.selectedDay,
                        processId: this.selectedProcess,
                    }
                case 'Entered, Produced and Accumulated All Processes':
                    return {
                        reportDate: this.selectedMonth,
                    }
                default:
                    return {
                        startDate: this.startDate,
                        endDate: this.endDate,
                    }
            }
        },
        async generateReport() {
            try {
                this.loading = true
                this.downloadUrl = null
                this.downloadFileName = null
                this.downloadExpiresAt = null
                this.linkCopied = false

                const payload = this.buildReportPayload()
                const result = await generateWorkOrderReport(
                    this.reportType,
                    payload
                )

                this.downloadUrl = result.downloadUrl
                this.downloadFileName = result.fileName
                this.downloadExpiresAt = result.expiresAt
                    ? moment(result.expiresAt).format('YYYY-MM-DD HH:mm')
                    : null
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },

        async copyDownloadLink() {
            if (!this.downloadUrl) return
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(this.downloadUrl)
                } else {
                    const textArea = document.createElement('textarea')
                    textArea.value = this.downloadUrl
                    textArea.style.position = 'fixed'
                    textArea.style.opacity = '0'
                    document.body.appendChild(textArea)
                    textArea.focus()
                    textArea.select()
                    document.execCommand('copy')
                    document.body.removeChild(textArea)
                }
                this.linkCopied = true
                setTimeout(() => {
                    this.linkCopied = false
                }, 2000)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Could not copy the link to the clipboard.',
                })
            }
        },

        /* =================================================================
         * LEGACY CLIENT-SIDE REPORT GENERATION — kept for hotfix reference.
         * =================================================================
         * Superseded by the Cloud Function based flow (`generateReport`
         * above + `@/services/reportsApi`), which builds the report
         * server-side and returns a signed download URL instead of
         * generating the .xlsx file in the browser with the `xlsx` package.
         *
         * This whole block is intentionally commented out:
         *   - Method names collide with the ones defined above
         *     (e.g. `generateReport`), so it can't just be uncommented.
         *   - It depends on `API` (from '@/services/api') and `XLSX`
         *     (from 'xlsx/xlsx.mjs'), whose imports are also commented
         *     out near the top of this file.
         *
         * To hotfix back to the old behavior: uncomment this block AND the
         * old imports above, then remove/rename the new report methods so
         * there's only one `generateReport` (and friends) left active.
         *
        // Report generation methods
        async generateReport() {
            if (this.reportType == 'By Status') {
                return this.generateReportByStatus()
            } else if (this.reportType == 'Produced KG Per Process') {
                return this.generateReportProducedByKgPerProcess()
            } else if (this.reportType == 'Per Product Type') {
                return this.generateReportProductType()
            } else if (this.reportType == 'Per Product') {
                return this.generateReportProduct()
            } else if (this.reportType == 'Shipped Per Month') {
                return this.generateReportShippedPerMonth()
            } else if (this.reportType == 'Per Process') {
                return this.generateReportPerProcesses()
            } else if (
                this.reportType ==
                'Entered, Produced and Accumulated Per Process'
            ) {
                return this.generateEnteredProducedAccumulatedPerProcess()
            } else if (
                this.reportType ==
                'Entered, Produced and Accumulated All Processes'
            ) {
                return this.generateEnteredProducedAccumulatedPerAllProcesses()
            } else if (this.reportType == 'Rejection') {
                return this.generateRejectionReport()
            } else if (this.reportType == 'Rejected Work Orders') {
                return this.generateRejectedWorkOrdersReport()
            } else if (this.reportType == 'History of Generated WO') {
                return this.generateHistoryOfGeneratedWO()
            }
        },
        async generateReportByStatus() {
            try {
                this.loading = true
                let workOrders = await API.getWorkOrdersByStatus({
                    startDate: this.startDate,
                    endDate: this.endDate,
                    status: this.selectedStatus,
                })
                if (workOrders.length > 0) {
                    let data = []
                    for (let workOrder of workOrders) {
                        const project = this.projects[workOrder.projectId]
                        const woInfo = {
                            order: workOrder.code,
                            project: project ? project.name || '' : '',
                            projectManager: this.userNames[
                                project?.projectManager
                            ],
                            designer: this.userNames[workOrder.createdBy],
                            status: workOrder.status,
                            lastUpdated: moment(workOrder.timestamp).format(
                                'YYYY-MM-DD'
                            ),
                        }
                        data.push({
                            Order: woInfo.order,
                            Project: woInfo.project,
                            'Project Manager': woInfo.projectManager,
                            Designer: woInfo.designer,
                            'Last Update In Range': woInfo.lastUpdated,
                            Status: woInfo.status,
                        })
                    }
                    // create XLSX
                    const worksheet = XLSX.utils.json_to_sheet(data)
                    const workbook = XLSX.utils.book_new()
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'clients')
                    XLSX.writeFile(
                        workbook,
                        `${this.selectedStatus.join(', ')} WO-Report.xlsx`
                    )
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        async generateReportProducedByKgPerProcess() {
            try {
                this.loading = true
                let report = await API.getProducedKgPerProcessReport({
                    startDate: this.startDate,
                    endDate: this.endDate,
                })
                // API returns calendar ranges with prior months for context; skip them.
                const allRanges = report.ranges || []
                const skipCount = this.getContextRangeSkipCount(
                    report,
                    this.startDate
                )
                const rangesToShow = report.ranges.slice(skipCount)
                const rangeIndices = rangesToShow.map((_, i) => i)
                const rangesStrings = rangesToShow.map(r =>
                    r.end ? `${r.start} to ${r.end}` : `${r.start}`
                )
                const reportTitleFrom = rangesToShow[0]?.start || this.startDate
                const lastRange = rangesToShow[rangesToShow.length - 1]
                const reportTitleTo =
                    lastRange?.end || lastRange?.start || this.endDate

                // Build data structures
                const data1 = {}
                const data2 = {}
                const processesFormattedData = {}
                const excludedProcessIds = ['quality', 'packing']

                for (const rangeIndex in rangesToShow) {
                    const rangeData = rangesToShow[rangeIndex]
                    const i = Number(rangeIndex)

                    // data1: entered metrics + previous month production
                    const prevIdx = skipCount + i - 1
                    const kgProducedPrevMonth =
                        prevIdx >= 0
                            ? Number(allRanges[prevIdx]?.effectiveProduction) ||
                              0
                            : 0
                    data1[rangeIndex] = {
                        ...this.buildEnteredRangeData(rangeData),
                        'KG Produced On Previous Month': kgProducedPrevMonth,
                    }

                    // data2: fabricated metrics
                    const kgEntered = rangeData.kgEnteredInMonth || 0
                    const pending = rangeData.pendingEffective || 0
                    const totalEntered = kgEntered + pending
                    const effectiveProduction =
                        rangeData.effectiveProduction || 0
                    data2[rangeIndex] = {
                        'Total Produced Effective (KG)': effectiveProduction,
                        'Total Produced In All Processes (KG)':
                            rangeData.kgProducedInMonthAllProcesses || 0,
                        '% Executed vs Entered':
                            totalEntered > 0
                                ? (effectiveProduction * 100) / totalEntered
                                : 0,
                    }

                    // process breakdown from processData
                    const pd = rangeData.processData
                    const kgAllProcesses =
                        rangeData.kgProducedInMonthAllProcesses || 0
                    const pdKeys = Array.isArray(pd)
                        ? pd.map(p => p.processId)
                        : Object.keys(pd || {})
                    pdKeys
                        .filter(id => !excludedProcessIds.includes(id))
                        .forEach(processId => {
                            if (!processesFormattedData[processId]) {
                                processesFormattedData[processId] = {}
                            }
                            const processDataItem = Array.isArray(pd)
                                ? pd.find(p => p.processId === processId)
                                : pd?.[processId]
                            const processedInRange =
                                processDataItem?.processedInRange ?? 0
                            processesFormattedData[processId][rangeIndex] = {
                                value: processedInRange,
                                percentage:
                                    kgAllProcesses > 0
                                        ? (processedInRange / kgAllProcesses) *
                                          100
                                        : 0,
                            }
                        })
                }

                // Section 1 — Entered
                const title1 = [
                    [
                        `Produced KG per process from ${reportTitleFrom} to ${reportTitleTo}`,
                    ],
                ]
                const headers1 = [['Entered', ...rangesStrings, 'Total']]
                const metrics1 = [
                    { name: 'WO Quantity' },
                    { name: 'References Quantity' },
                    { name: 'Pieces Quantity' },
                    { name: 'KG Produced On Previous Month', fixed: 1 },
                    {
                        name: 'KG Entered to Production This Range',
                        fixed: 1,
                    },
                    {
                        name: 'KG Accumulated From Past Month',
                        fixed: 1,
                        noTotal: true,
                    },
                    {
                        name: 'Total KG Entered to Production',
                        fixed: 1,
                        noTotal: true,
                    },
                ]
                const data1Rows = this.getMetricsByMonth(
                    metrics1,
                    rangeIndices,
                    data1
                )

                // Section 2 — Fabricated
                const title2 = [
                    [
                        `Fabricated per process from ${reportTitleFrom} to ${reportTitleTo}`,
                    ],
                ]
                const headers2 = [['Fabricated', ...rangesStrings, 'Total']]
                const metrics2 = [
                    { name: 'Total Produced Effective (KG)', fixed: 1 },
                    {
                        name: 'Total Produced In All Processes (KG)',
                        fixed: 1,
                    },
                    { name: '% Executed vs Entered', fixed: 1, noTotal: true },
                ]
                const data2Rows = this.getMetricsByMonth(
                    metrics2,
                    rangeIndices,
                    data2
                )

                // Section 3 — Process breakdown
                const title3 = [
                    [
                        `Process Breakdown from ${reportTitleFrom} to ${reportTitleTo}`,
                    ],
                ]
                const headers3 = [['Process', ...rangesStrings, 'Total']]
                const processRows = this.formatValueAndPercentageRows(
                    processesFormattedData,
                    rangeIndices,
                    processId => this.processesNames?.[processId] || processId,
                    { valueFixed: 1, percentageFixed: 1 }
                )

                const spacer = [['']]
                const sheetData = [
                    ...title1,
                    ...headers1,
                    ...data1Rows,
                    ...spacer,
                    ...title2,
                    ...headers2,
                    ...data2Rows,
                    ...spacer,
                    ...title3,
                    ...headers3,
                    ...processRows,
                ]

                this.writeXlsxReport(
                    sheetData,
                    'WO-Produced-KG-Per-Process-Report.xlsx'
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        async generateReportProductType() {
            try {
                this.loading = true
                let report = await API.getProductTypeReport({
                    startDate: this.startDate,
                    endDate: this.endDate,
                })
                const skipCount = this.getContextRangeSkipCount(
                    report,
                    this.startDate
                )
                const rangesToShow = report.ranges.slice(skipCount)
                const rangeIndices = rangesToShow.map((_, i) => i)
                const rangesStrings = rangesToShow.map(
                    r => `${r.start} to ${r.end}`
                )

                // Build data structures
                let data1 = {}
                let data2 = {}
                let productTypesFormattedData = {}

                for (let rangeIndex in rangesToShow) {
                    const rangeData = rangesToShow[rangeIndex]

                    // data1: entered metrics (shared structure)
                    data1[rangeIndex] = this.buildEnteredRangeData(rangeData)

                    // data2: fabricated metrics
                    const kgEntered = rangeData.kgEnteredInMonth || 0
                    const pending = rangeData.pendingEffective || 0
                    const totalEntered = kgEntered + pending
                    const effectiveProduction =
                        rangeData.effectiveProduction || 0
                    data2[rangeIndex] = {
                        'Total Produced Effective (KG)': effectiveProduction,
                        'Total Produced In All Processes (KG)':
                            rangeData.kgProducedInMonthAllProcesses || 0,
                        '% Executed vs Entered':
                            totalEntered > 0
                                ? (effectiveProduction * 100) / totalEntered
                                : 0,
                    }

                    // product type breakdown from range.productTypeData
                    const ptd = rangeData.productTypeData || {}
                    const kgAllProcesses =
                        rangeData.kgProducedInMonthAllProcesses || 0
                    Object.keys(ptd).forEach(productTypeId => {
                        if (!productTypesFormattedData[productTypeId]) {
                            productTypesFormattedData[productTypeId] = {}
                        }
                        const processedInRange =
                            ptd[productTypeId].processedInRange || 0
                        productTypesFormattedData[productTypeId][rangeIndex] = {
                            value: processedInRange,
                            percentage:
                                kgAllProcesses > 0
                                    ? (processedInRange / kgAllProcesses) * 100
                                    : 0,
                        }
                    })
                }

                // Section 1 — Entered
                const title1 = [
                    [
                        `Produced KG per product type from ${
                            rangesToShow[0].start
                        } to ${rangesToShow[rangesToShow.length - 1].end}`,
                    ],
                ]
                const headers1 = [['Entered', ...rangesStrings, 'Total']]
                const metrics1 = [
                    { name: 'WO Quantity', fixed: 2 },
                    { name: 'References Quantity', fixed: 2 },
                    { name: 'Pieces Quantity', fixed: 2 },
                    {
                        name: 'KG Entered to Production This Range',
                        fixed: 2,
                    },
                    {
                        name: 'KG Accumulated From Past Month',
                        fixed: 2,
                        noTotal: true,
                    },
                    {
                        name: 'Total KG Entered to Production',
                        fixed: 2,
                        noTotal: true,
                    },
                ]
                const data1Rows = this.getMetricsByMonth(
                    metrics1,
                    rangeIndices,
                    data1
                )

                // Section 2 — Fabricated
                const title2 = [
                    [
                        `Fabricated per product type from ${
                            rangesToShow[0].start
                        } to ${rangesToShow[rangesToShow.length - 1].end}`,
                    ],
                ]
                const headers2 = [['Fabricated', ...rangesStrings, 'Total']]
                const metrics2 = [
                    { name: 'Total Produced Effective (KG)', fixed: 2 },
                    {
                        name: 'Total Produced In All Processes (KG)',
                        fixed: 2,
                    },
                    {
                        name: '% Executed vs Entered',
                        fixed: 2,
                        noTotal: true,
                    },
                ]
                const data2Rows = this.getMetricsByMonth(
                    metrics2,
                    rangeIndices,
                    data2
                )

                // Section 3 — Product type breakdown
                const title3 = [
                    [
                        `Product Type Breakdown from ${
                            rangesToShow[0].start
                        } to ${rangesToShow[rangesToShow.length - 1].end}`,
                    ],
                ]
                const headers3 = [['Product Type', ...rangesStrings, 'Total']]
                const productTypesRows = this.formatValueAndPercentageRows(
                    productTypesFormattedData,
                    rangeIndices,
                    productTypeId =>
                        this.productTypes?.[productTypeId] || productTypeId,
                    { valueFixed: 2, percentageFixed: 2 }
                )

                const spacer = [['']]

                const sheetData = [
                    ...title1,
                    ...headers1,
                    ...data1Rows,
                    ...spacer,
                    ...title2,
                    ...headers2,
                    ...data2Rows,
                    ...spacer,
                    ...title3,
                    ...headers3,
                    ...productTypesRows,
                ]

                this.writeXlsxReport(sheetData, 'WO-Product-Type-Report.xlsx')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        async generateReportProduct() {
            try {
                this.loading = true
                let report = await API.getProductReport({
                    startDate: this.startDate,
                    endDate: this.endDate,
                })

                const skipCount = this.getContextRangeSkipCount(
                    report,
                    this.startDate
                )
                const rangesToShow = report.ranges.slice(skipCount)
                const rangeIndices = rangesToShow.map((_, i) => i)
                const rangesStrings = rangesToShow.map(
                    r => `${r.start} to ${r.end}`
                )

                // Build data structures
                let data1 = {}
                let data2 = {}
                let materialTypesFormattedData = {}

                for (let rangeIndex in rangesToShow) {
                    const rangeData = rangesToShow[rangeIndex]

                    // data1: entered metrics (shared structure)
                    data1[rangeIndex] = this.buildEnteredRangeData(rangeData)

                    // data2: fabricated metrics
                    const kgEntered = rangeData.kgEnteredInMonth || 0
                    const pending = rangeData.pendingEffective || 0
                    const totalEntered = kgEntered + pending
                    const effectiveProduction =
                        rangeData.effectiveProduction || 0
                    data2[rangeIndex] = {
                        'Total Produced Effective (KG)': effectiveProduction,
                        'Total Produced In All Processes (KG)':
                            rangeData.kgProducedInMonthAllProcesses || 0,
                        '% Executed vs Entered':
                            totalEntered > 0
                                ? (effectiveProduction * 100) / totalEntered
                                : 0,
                    }

                    // product breakdown from range.productData
                    const mtd = rangeData.productData || {}
                    const kgAllProcesses =
                        rangeData.kgProducedInMonthAllProcesses || 0
                    Object.keys(mtd).forEach(materialTypeId => {
                        if (!materialTypesFormattedData[materialTypeId]) {
                            materialTypesFormattedData[materialTypeId] = {}
                        }
                        const processedInRange =
                            mtd[materialTypeId].processedInRange || 0
                        materialTypesFormattedData[materialTypeId][
                            rangeIndex
                        ] = {
                            value: processedInRange,
                            percentage:
                                kgAllProcesses > 0
                                    ? (processedInRange / kgAllProcesses) * 100
                                    : 0,
                        }
                    })
                }

                // Section 1 — Entered
                const title1 = [
                    [
                        `Produced KG per product from ${
                            rangesToShow[0].start
                        } to ${rangesToShow[rangesToShow.length - 1].end}`,
                    ],
                ]
                const headers1 = [['Entered', ...rangesStrings, 'Total']]
                const metrics1 = [
                    { name: 'WO Quantity', fixed: 2 },
                    { name: 'References Quantity', fixed: 2 },
                    { name: 'Pieces Quantity', fixed: 2 },
                    {
                        name: 'KG Entered to Production This Range',
                        fixed: 2,
                    },
                    {
                        name: 'KG Accumulated From Past Month',
                        fixed: 2,
                        noTotal: true,
                    },
                    {
                        name: 'Total KG Entered to Production',
                        fixed: 2,
                        noTotal: true,
                    },
                ]
                const data1Rows = this.getMetricsByMonth(
                    metrics1,
                    rangeIndices,
                    data1
                )

                // Section 2 — Fabricated
                const title2 = [
                    [
                        `Fabricated per product from ${
                            rangesToShow[0].start
                        } to ${rangesToShow[rangesToShow.length - 1].end}`,
                    ],
                ]
                const headers2 = [['Fabricated', ...rangesStrings, 'Total']]
                const metrics2 = [
                    { name: 'Total Produced Effective (KG)', fixed: 2 },
                    {
                        name: 'Total Produced In All Processes (KG)',
                        fixed: 2,
                    },
                    {
                        name: '% Executed vs Entered',
                        fixed: 2,
                        noTotal: true,
                    },
                ]
                const data2Rows = this.getMetricsByMonth(
                    metrics2,
                    rangeIndices,
                    data2
                )

                // Section 3 — Product type breakdown
                const title3 = [
                    [
                        `Product Breakdown from ${rangesToShow[0].start} to ${
                            rangesToShow[rangesToShow.length - 1].end
                        }`,
                    ],
                ]
                const headers3 = [['Product', ...rangesStrings, 'Total']]
                const productsRows = this.formatValueAndPercentageRows(
                    materialTypesFormattedData,
                    rangeIndices,
                    productAbbreviation =>
                        this.products?.[productAbbreviation] ||
                        productAbbreviation,
                    { valueFixed: 2, percentageFixed: 2 }
                )

                const spacer = [['']]

                const sheetData = [
                    ...title1,
                    ...headers1,
                    ...data1Rows,
                    ...spacer,
                    ...title2,
                    ...headers2,
                    ...data2Rows,
                    ...spacer,
                    ...title3,
                    ...headers3,
                    ...productsRows,
                ]

                this.writeXlsxReport(sheetData, 'WO-Product-Report.xlsx')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        async generateReportShippedPerMonth() {
            try {
                this.loading = true

                let report = await API.getShippedPerMonthReport({
                    startDate: this.startDate,
                    endDate: this.endDate,
                })
                // The API prepends prior months for context (always full
                // calendar months here, since Shipped Per Month always starts
                // on the 1st) — skip them.
                const rangesToShow = report.ranges.slice(
                    report.contextMonths ?? 3
                )

                // Numeric indices used as consistent keys across all data structures
                // and passed to helper functions instead of range objects.
                const rangeIndices = rangesToShow.map((_, i) => i)

                // Get ranges strings to show in report
                const rangesStrings = rangesToShow.map(
                    r => `${r.start} to ${r.end}`
                )

                // Title
                const title1 = [
                    [
                        `Shipped Per Month Report from ${
                            rangesToShow[0].start
                        } to ${rangesToShow[rangesToShow.length - 1].end}`,
                    ],
                ]

                // Headers for first table
                const headers1 = [['Entered', ...rangesStrings, 'Total']]

                // Prepare data structures
                let data1 = {}
                let data2 = {}
                let productTypesData = {}
                let energiaSolarDataByMonth = {}
                let otherMetricsDataByMonth = {}

                // Process monthly data
                for (let rangeIndex in rangesToShow) {
                    const rangeData = rangesToShow[rangeIndex]
                    const deliveredData = rangeData.deliveredData || {}

                    // Build data1 (entered data)
                    data1[rangeIndex] = this.buildEnteredRangeData(rangeData)

                    // Build data2 (produced and delivered data)
                    data2[rangeIndex] = {
                        'Total Produced (KG)':
                            rangeData.effectiveProduction || 0,
                        '% Shipped vs Entered':
                            deliveredData.executedVsEntered || 0,
                    }

                    // Prepare energia solar data
                    energiaSolarDataByMonth[rangeIndex] = {
                        'Kg Delivered to Energia Solar': {
                            value:
                                deliveredData.energiaSolar?.totalDeliveredKg ||
                                0,
                            percentage:
                                deliveredData.energiaSolar
                                    ?.totalDeliveredKgPercentage || 0,
                        },
                    }

                    // Prepare other metrics data
                    otherMetricsDataByMonth[rangeIndex] = {
                        'Other Clients Total Kg': {
                            value:
                                deliveredData.otherClientsTotalKg?.value || 0,
                            percentage:
                                deliveredData.otherClientsTotalKg?.percentage ||
                                0,
                        },
                        'Exported KG': {
                            value: deliveredData.exportedKg?.value || 0,
                            percentage:
                                deliveredData.exportedKg?.percentage || 0,
                        },
                        'National Projects': {
                            value: deliveredData.nationalKg?.value || 0,
                            percentage:
                                deliveredData.nationalKg?.percentage || 0,
                        },
                    }

                    // Process product types for Energia Solar
                    if (deliveredData.energiaSolar?.totalByProductType) {
                        Object.keys(
                            deliveredData.energiaSolar.totalByProductType
                        ).forEach(productType => {
                            if (!productTypesData[productType]) {
                                productTypesData[productType] = {}
                            }
                            productTypesData[productType][rangeIndex] =
                                deliveredData.energiaSolar.totalByProductType[
                                    productType
                                ]
                        })
                    }
                }

                // Format data1 in rows
                const metrics1 = [
                    { name: 'WO Quantity', fixed: 2 },
                    { name: 'References Quantity', fixed: 2 },
                    { name: 'Pieces Quantity', fixed: 2 },
                    { name: 'KG Entered to Production This Range', fixed: 2 },
                    {
                        name: 'KG Accumulated From Past Month',
                        fixed: 2,
                        noTotal: true,
                    },
                    {
                        name: 'Total KG Entered to Production',
                        fixed: 2,
                        noTotal: true,
                    },
                ]
                const data1Rows = this.getMetricsByMonth(
                    metrics1,
                    rangeIndices,
                    data1
                )

                // Format data2 in rows - separate metrics
                const metrics2Before = [
                    { name: 'Total Produced (KG)', fixed: 2 },
                    { name: '% Shipped vs Entered', fixed: 2 },
                ]
                const data2RowsBefore = this.getMetricsByMonth(
                    metrics2Before,
                    rangeIndices,
                    data2
                )

                // Reorganize energia solar data for formatValueAndPercentageRows
                const energiaSolarFormattedData = {}
                rangeIndices.forEach(rangeIndex => {
                    const monthData = energiaSolarDataByMonth[rangeIndex] || {}
                    Object.keys(monthData).forEach(key => {
                        if (!energiaSolarFormattedData[key]) {
                            energiaSolarFormattedData[key] = {}
                        }
                        energiaSolarFormattedData[key][rangeIndex] =
                            monthData[key]
                    })
                })

                // Format "Kg Delivered to Energia Solar" using the reusable function
                const energiaSolarRows = this.formatValueAndPercentageRows(
                    energiaSolarFormattedData,
                    rangeIndices,
                    key => key,
                    {
                        valueMeasure: '',
                        valueFixed: 2,
                        percentageFixed: 2,
                    }
                )

                // Format product types as rows using the reusable function
                const productTypesRows = this.formatValueAndPercentageRows(
                    productTypesData,
                    rangeIndices,
                    productTypeKey =>
                        this.productTypes[productTypeKey] || productTypeKey,
                    {
                        valueMeasure: '',
                        valueFixed: 2,
                        percentageFixed: 2,
                    }
                )

                // Reorganize other metrics data for formatValueAndPercentageRows
                const otherMetricsFormattedData = {}
                rangeIndices.forEach(rangeIndex => {
                    const monthData = otherMetricsDataByMonth[rangeIndex] || {}
                    Object.keys(monthData).forEach(key => {
                        if (!otherMetricsFormattedData[key]) {
                            otherMetricsFormattedData[key] = {}
                        }
                        otherMetricsFormattedData[key][rangeIndex] =
                            monthData[key]
                    })
                })

                // Format other metrics using the reusable function
                const otherMetricsRows = this.formatValueAndPercentageRows(
                    otherMetricsFormattedData,
                    rangeIndices,
                    key => key,
                    {
                        valueMeasure: '',
                        valueFixed: 2,
                        percentageFixed: 2,
                    }
                )

                const spacer = [['']]

                // Title for shipped by client
                const title2 = [
                    [
                        `Shipped By Client from ${rangesToShow[0].start} to ${
                            rangesToShow[rangesToShow.length - 1].end
                        }`,
                    ],
                ]

                // Headers for second table
                const headers2 = [['Shipped', ...rangesStrings, 'Total']]

                // XLSX data
                const sheetData = [
                    ...title1,
                    ...headers1,
                    ...data1Rows,
                    ...spacer,
                    ...title2,
                    ...headers2,
                    ...data2RowsBefore,
                    ...spacer,
                    ...energiaSolarRows,
                    ...productTypesRows,
                    ...spacer,
                    ...otherMetricsRows,
                ]

                this.writeXlsxReport(sheetData, 'Shipped-Per-Month-Report.xlsx')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        // you can select the process and the time ranges
        async generateReportPerProcesses() {
            try {
                this.loading = true
                let report = await API.getPerProcessReport({
                    startDate: this.startDate,
                    endDate: this.endDate,
                })

                const workbook = XLSX.utils.book_new()

                // Data Per Day
                const processData = report.ranges.map(range => ({
                    Date: range.start,
                    Entered:
                        range.processData[this.selectedProcess]
                            ?.enteredInRange || 0,
                    Processed:
                        range.processData[this.selectedProcess]
                            ?.processedInRange || 0,
                }))

                // Calculate totals
                const totalAvailable = processData.reduce(
                    (sum, row) => sum + row.Entered,
                    0
                )
                const totalProduced = processData.reduce(
                    (sum, row) => sum + row.Processed,
                    0
                )

                // Total Row
                processData.push({
                    Date: 'TOTAL',
                    Entered: totalAvailable,
                    Processed: totalProduced,
                })

                const worksheet = XLSX.utils.json_to_sheet(processData, {
                    header: ['Date', 'Entered', 'Processed'],
                })

                // Sheet name
                let sheetName =
                    this.processesNames?.[this.selectedProcess] || 'Process'
                if (sheetName.length > 31)
                    sheetName = sheetName.slice(0, 28) + '...'

                XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

                XLSX.writeFile(
                    workbook,
                    `${this.processesNames[this.selectedProcess]}-Report.xlsx`
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        // you can select the process and the time ranges and see detailed information.
        async generateEnteredProducedAccumulatedPerProcess() {
            try {
                this.loading = true

                // set selectedDay
                const selectedDay = this.selectedDay
                const report = await API.getEnteredProducedAndAccumulatedPerProcessReport(
                    {
                        reportDate: this.selectedDay,
                        processId: this.selectedProcess,
                    }
                )

                // Generate the XLSx report
                const sheetData = [
                    [
                        `PROCESSED KG REPORT - (${report.processName})`,
                        `${selectedDay}`,
                    ],
                    [
                        'Past Month Value',
                        `${report.pastMonthValue} Kg` ?? '0 Kg',
                    ],
                    ['Entered in Day', `${report.enteredInDay} Kg` ?? '0 Kg'],
                    [
                        'Total Entered In Month',
                        `${report.enteredInMonth} Kg` ?? '0 Kg',
                    ],
                    [
                        'Total Accumulated',
                        `${report.totalAccumulated} Kg` ?? '0 Kg',
                    ],
                    [
                        'Processed Today',
                        `${report.processedToday} Kg` ?? '0 Kg',
                    ],
                    [
                        'Processed This Month',
                        `${report.processedThisMonth} Kg` ?? '0 Kg',
                    ],
                    ['Available', `${report.available} Kg` ?? '0 Kg'],
                    [
                        'Minimum Expected Capacity In The Month',
                        `${report.minimumExpectedCapacityInMonth} Kg` ?? '0 Kg',
                    ],
                    [
                        'Minimum Expected Capacity Today',
                        `${report.minimumExpectedCapacityToday} Kg` ?? '0 Kg',
                    ],
                    [
                        '% Occupation in Month',
                        `${report.occupationInMonth}%` ?? '0%',
                    ],
                    [
                        '% Cumulative Efficiency In The Month',
                        `${report.cummulativeEfficiencyInMonth}%` ?? '0%',
                    ],
                    [
                        '% Cumulative Efficiency Today',
                        `${report.cummulativeEfficiencyToday}%` ?? '0%',
                    ],
                    [
                        'Labor Value In Day ( 8 Hours )',
                        `${report.laborValue}$` ?? '0$',
                    ],
                    [
                        'Labor Value / Processed KG',
                        `${report.laborValuePerProcessedKg}$` ?? '0$',
                    ],
                ]

                this.writeXlsxReport(
                    sheetData,
                    `${report.processName}-Production-Report.xlsx`
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        // you can select the months and the report will generate tables for every process.
        async generateEnteredProducedAccumulatedPerAllProcesses() {
            try {
                this.loading = true

                const report = await API.getEnteredProducedAndAccumulatedPerAllProcessesReport(
                    {
                        reportDate: this.selectedMonth,
                    }
                )

                // Generate the report
                const worksheet = XLSX.utils.aoa_to_sheet([])
                let startRow = 0

                for (const process of Object.values(report.processes)) {
                    const table = [
                        [`${process.processName}`, `${report.reportDate}`],
                        ['Past Month Value', `${process.pastMonthValue} Kg`],
                        ['Entered In Month', `${process.enteredInMonth} Kg`],
                        ['Available In Month', `${process.available ?? 0} Kg`],
                        [
                            'Processed This Month',
                            `${process.processedThisMonth} Kg`,
                        ],
                        [
                            'Minimum Expected Capacity In The Month',
                            `${process.minimumExpectedCapacityInMonth} Kg`,
                        ],
                        [
                            '% Occupation in Month',
                            `${process.occupationInMonth}%`,
                        ],
                        [
                            '% Efficiency In The Month',
                            `${process.cummulativeEfficiencyInMonth}%`,
                        ],
                        ['Labor Value', `$${process.laborValue}`],
                        [
                            'Labor Value / Processed KG',
                            `${process.laborValuePerProcessedKg}`,
                        ],
                    ]

                    // Agrega tabla al worksheet con offset
                    XLSX.utils.sheet_add_aoa(worksheet, table, {
                        origin: `A${startRow + 1}`,
                    })

                    startRow += table.length + 1
                }

                const workbook = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(workbook, worksheet)
                XLSX.writeFile(workbook, 'All-Processes-Production-Report.xlsx')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        // generate rejection report
        async generateRejectionReport() {
            try {
                this.loading = true
                const historyByDesigner = await API.getRejectionReport({
                    startDate: this.startDate,
                    endDate: this.endDate,
                })

                const wb = XLSX.utils.book_new()

                // Get all unique rejection reasons across designers
                const allRejectionReasons = new Set()

                Object.values(historyByDesigner).forEach(data => {
                    Object.keys(data).forEach(key => {
                        if (
                            key !== 'createdWO' &&
                            key !== 'rejectedWO' &&
                            key !== 'projectManagerMistakes' &&
                            key !== 'totalRejections'
                        ) {
                            allRejectionReasons.add(key)
                        }
                    })
                })

                const sortedReasons = Array.from(allRejectionReasons).sort()

                // Create worksheet data with headers
                const mainData = [
                    [
                        'Designer',
                        'Created Work Orders',
                        'Rejected Work Orders',
                        'Total Rejections',
                        'Project Manager Mistakes',
                        ...sortedReasons,
                    ],
                ]

                // Populate data for each designer
                Object.entries(historyByDesigner).forEach(
                    ([designerId, data]) => {
                        const row = [
                            this.userNames[designerId] || 'not found',
                            data.createdWO || 0,
                            data.rejectedWO || 0,
                            data.totalRejections || 0,
                            data.projectManagerMistakes || 0,
                        ]

                        // Add counts for each rejection reason
                        sortedReasons.forEach(reason => {
                            row.push(data[reason] || 0)
                        })

                        mainData.push(row)
                    }
                )

                // Create worksheet
                const wsMain = XLSX.utils.aoa_to_sheet(mainData)

                // Set column widths
                const colWidths = [
                    { wch: 25 }, // Designer
                    { wch: 18 }, // Created Work Orders
                    { wch: 18 }, // Rejected Work Orders
                    { wch: 18 }, // Total Rejections
                    { wch: 22 }, // Project Manager Mistakes
                    ...sortedReasons.map(() => ({ wch: 20 })), // Rejection reasons
                ]
                wsMain['!cols'] = colWidths

                // Add sheet to workbook and save
                XLSX.utils.book_append_sheet(wb, wsMain, 'Rejection Report')
                XLSX.writeFile(wb, 'Rejection-Report.xlsx')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        async generateRejectedWorkOrdersReport() {
            try {
                this.loading = true
                const rejectedWorkOrders = await API.generateRejectedWorkOrdersReport(
                    {
                        startDate: this.startDate,
                        endDate: this.endDate,
                    }
                )

                // Sort data chronologically by rejection date
                const sortedData = rejectedWorkOrders.sort((a, b) => {
                    const dateA = this.parseDate(a.rejectedOn)
                    const dateB = this.parseDate(b.rejectedOn)
                    return dateA - dateB
                })

                // Prepare data for Excel with professional column names
                const sheetData = [
                    [
                        'ID',
                        'Entered Date',
                        'Rejection Date',
                        'Rejected By',
                        'Generated',
                        'Project Manager',
                        'Designer',
                        'Work Order',
                        'Project Name',
                        'Category',
                    ],
                ]

                sortedData.forEach((item, index) => {
                    sheetData.push([
                        index + 1,
                        item.createdOn,
                        item.rejectedOn,
                        item.rejectedBy,
                        item.generated,
                        item.projectManager,
                        item.designer,
                        item.order,
                        item.projectName,
                        item.rejectionReason,
                    ])
                })

                // Generate Excel file
                this.writeXlsxReport(
                    sheetData,
                    'REJECTED-WORK-ORDERS-REPORT.xlsx',
                    'Rejected Work Orders'
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        async generateHistoryOfGeneratedWO() {
            try {
                this.loading = true
                const historyOfGeneratedWO = await API.generateHistoryOfGeneratedWO(
                    {
                        startDate: this.startDate,
                        endDate: this.endDate,
                    }
                )

                // Prepare data for Excel
                const sheetData = [
                    [
                        'id',
                        'Entered Date',
                        'PM',
                        'DESIGNERS',
                        'ORDER',
                        'PROJECT',
                    ],
                ]

                historyOfGeneratedWO.forEach((workOrder, index) => {
                    sheetData.push([
                        index + 1,
                        workOrder.createdOn,
                        workOrder.projectManager,
                        workOrder.createdBy,
                        workOrder.code,
                        workOrder.projectName,
                    ])
                })

                // Generate Excel file
                this.writeXlsxReport(
                    sheetData,
                    'HISTORY-OF-GENERATED-WO-REPORT.xlsx',
                    'History'
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeWoReport()
            }
        },
        // Functions for report generation
        async getHistoryLog(date) {
            try {
                let historyLog = await API.getHistoryLog(date)
                // if the month dont have the log. create it.
                if (!historyLog) {
                    historyLog = await API.createWorkOrderHistoryLog(date)
                }
                // Convert IDs to process names
                const keysToConvert = [
                    'availableQty',
                    'productionQty',
                    'deliveredQty',
                    'availableKg',
                    'productionKg',
                    'deliveredKg',
                ]
                keysToConvert.forEach(key => {
                    if (historyLog[key]) {
                        historyLog[key] = this.convertProcessIdsToNames(
                            historyLog[key]
                        )
                    }
                })
                return historyLog
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        convertProcessIdsToNames(dataObj) {
            const result = {}
            if (Object.values(dataObj).length > 0) {
                Object.entries(dataObj).forEach(([id, value]) => {
                    const name = this.processesNames[id]
                    if (name) result[name] = value
                })
            }
            return result
        },
        // Number of leading ranges in report.ranges that are only "context"
        // months (before the user-selected startDate) and must be skipped
        // when displaying the report. Falls back to the legacy value (3) if
        // an older API response doesn't include contextMonths yet.
        getContextRangeSkipCount(report, startDateStr) {
            const contextMonths = report.contextMonths ?? 3
            const isFirstOfMonth = startDateStr.split('-')[2] === '01'
            return contextMonths + (isFirstOfMonth ? 0 : 1)
        },
        getMonthsInRange(start, end) {
            const months = []
            const current = moment.utc(start).startOf('month')
            const endMonth = moment.utc(end).startOf('month')

            while (current.isSameOrBefore(endMonth)) {
                months.push(current.format('YYYY-MM'))
                current.add(1, 'month')
            }

            return months
        },
        getDaysInRange(start, end) {
            const days = []
            const current = moment.utc(start).startOf('day')
            const endDay = moment.utc(end).startOf('day')

            while (current.isSameOrBefore(endDay)) {
                days.push(current.format('YYYY-MM-DD'))
                current.add(1, 'day')
            }
            return days
        },
        buildEnteredRangeData(rangeData) {
            const kgEnteredInMonth = rangeData.kgEnteredInMonth || 0
            const pendingEffective = rangeData.pendingEffective || 0
            return {
                'WO Quantity': rangeData.enteredWorkOrders || 0,
                'References Quantity': rangeData.enteredReferences || 0,
                'Pieces Quantity': rangeData.enteredPieces || 0,
                'KG Entered to Production This Range': kgEnteredInMonth,
                'KG Accumulated From Past Month': pendingEffective,
                'Total KG Entered to Production':
                    kgEnteredInMonth + pendingEffective,
            }
        },
        writeXlsxReport(sheetData, filename, sheetName = 'Report') {
            const ws = XLSX.utils.aoa_to_sheet(sheetData)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, sheetName)
            XLSX.writeFile(wb, filename)
        },
        getMetricsByMonth(metrics, months, data) {
            return metrics.map(metric => {
                const row = [metric.name]
                for (let month of months) {
                    let value = data[month]?.[metric.name] || 0

                    if (metric.measure) {
                        // With a unit suffix: format as string
                        const formatted =
                            typeof metric.fixed === 'number'
                                ? parseFloat(value).toFixed(metric.fixed)
                                : value
                        row.push(`${formatted}${metric.measure}`)
                    } else if (typeof metric.fixed === 'number') {
                        // No suffix: emit plain number
                        row.push(
                            parseFloat(parseFloat(value).toFixed(metric.fixed))
                        )
                    } else {
                        row.push(value)
                    }
                }

                // Calculate total
                if (metric.noTotal) {
                    row.push('')
                } else {
                    let total = months.reduce((sum, month) => {
                        const raw = data[month]?.[metric.name] || 0
                        const num = parseFloat(raw)
                        return sum + (isNaN(num) ? 0 : num)
                    }, 0)

                    if (metric.measure) {
                        const formatted =
                            typeof metric.fixed === 'number'
                                ? total.toFixed(metric.fixed)
                                : total
                        row.push(`${formatted}${metric.measure}`)
                    } else if (typeof metric.fixed === 'number') {
                        row.push(parseFloat(total.toFixed(metric.fixed)))
                    } else {
                        row.push(total)
                    }
                }

                return row
            })
        },
        formatValueAndPercentageRows(
            itemsData,
            months,
            getNameFn,
            options = {}
        ) {
            const {
                valueMeasure = '',
                valueFixed = 2,
                percentageFixed = 2,
            } = options

            const rows = []
            const itemKeys = Object.keys(itemsData)

            itemKeys.forEach(itemKey => {
                const itemName = getNameFn(itemKey)
                const valueRow = [itemName]
                const percentageRow = ['%']
                let totalValue = 0
                let totalPercentage = 0

                months.forEach(month => {
                    const itemData = itemsData[itemKey]?.[month] || {
                        value: 0,
                        percentage: 0,
                    }
                    const value = itemData.value || 0
                    const percentage = itemData.percentage || 0

                    // Format value — emit plain number when no measure
                    const formattedValue = valueMeasure
                        ? `${parseFloat(value).toFixed(
                              valueFixed
                          )}${valueMeasure}`
                        : parseFloat(parseFloat(value).toFixed(valueFixed))
                    valueRow.push(formattedValue)
                    totalValue += value

                    // Format percentage — emit plain number
                    const formattedPercentage = parseFloat(
                        parseFloat(percentage).toFixed(percentageFixed)
                    )
                    percentageRow.push(formattedPercentage)
                    totalPercentage += percentage
                })

                // Add totals
                const formattedTotalValue = valueMeasure
                    ? `${parseFloat(totalValue).toFixed(
                          valueFixed
                      )}${valueMeasure}`
                    : parseFloat(parseFloat(totalValue).toFixed(valueFixed))
                valueRow.push(formattedTotalValue)

                const formattedTotalPercentage = parseFloat(
                    parseFloat(totalPercentage).toFixed(percentageFixed)
                )
                percentageRow.push(formattedTotalPercentage)

                rows.push(valueRow)
                rows.push(percentageRow)
            })

            return rows
        }, // Dates Functions
         */

        getDateRangeMaxDays() {
            if (this.reportType === 'Per Process') {
                return 60
            }
            return null
        },
        getDateRangeMaxMonths() {
            if (
                [
                    'Produced KG Per Process',
                    'Per Product Type',
                    'Per Product',
                    'By Status',
                    'Rejection',
                    'Rejected Work Orders',
                    'History of Generated WO',
                ].includes(this.reportType)
            ) {
                return 3
            }
            return null
        },
        getOrderedDateRange(startDate, endDate) {
            const start = moment(startDate)
            const end = moment(endDate)
            if (end.isBefore(start, 'day')) {
                return { start: end, end: start }
            }
            return { start, end }
        },
        getMaxAllowedEndDate(startDate) {
            const start = moment(startDate)
            const today = moment().startOf('day')
            let maxEnd = today.clone()

            const maxDays = this.getDateRangeMaxDays()
            if (maxDays) {
                const daysMaxEnd = start.clone().add(maxDays - 1, 'days')
                if (daysMaxEnd.isBefore(maxEnd)) {
                    maxEnd = daysMaxEnd
                }
            }

            const maxMonths = this.getDateRangeMaxMonths()
            if (maxMonths) {
                const monthsMaxEnd = start
                    .clone()
                    .add(maxMonths, 'months')
                    .subtract(1, 'day')
                if (monthsMaxEnd.isBefore(maxEnd)) {
                    maxEnd = monthsMaxEnd
                }
            }

            return maxEnd
        },
        clampDateRange(startDate, endDate) {
            const { start, end } = this.getOrderedDateRange(startDate, endDate)
            const maxEnd = this.getMaxAllowedEndDate(start)
            const clampedEnd = moment.min(end, maxEnd)
            return [start.format('YYYY-MM-DD'), clampedEnd.format('YYYY-MM-DD')]
        },
        isDateRangeWithinLimit() {
            return this.validateMaxRange() === true
        },
        onDateRangeInput() {
            if (this.dates && this.dates.length === 2) {
                const [startDate, endDate] = this.clampDateRange(
                    this.dates[0],
                    this.dates[1]
                )
                if (startDate !== this.dates[0] || endDate !== this.dates[1]) {
                    this.dates = [startDate, endDate]
                }
            }
        },
        validateMaxRange() {
            if (!this.dates || this.dates.length !== 2) return true
            const startDate = this.dates[0]
            const endDate = this.dates[1]
            if (!startDate || !endDate) return true

            const { start, end } = this.getOrderedDateRange(startDate, endDate)
            const today = moment().startOf('day')

            if (end.isAfter(today)) {
                return 'End date cannot be in the future'
            }

            const maxDays = this.getDateRangeMaxDays()
            if (maxDays && end.diff(start, 'days') + 1 > maxDays) {
                return `Date range cannot exceed ${maxDays} days`
            }

            const maxMonths = this.getDateRangeMaxMonths()
            if (maxMonths) {
                const maxEnd = start
                    .clone()
                    .add(maxMonths, 'months')
                    .subtract(1, 'day')
                if (end.isAfter(maxEnd)) {
                    return `Date range cannot exceed ${maxMonths} months`
                }
            }

            return true
        },
        onMonthlyRangeInput() {
            // Validate range when months are selected
            if (this.monthlyRangeDates && this.monthlyRangeDates.length === 2) {
                let [startMonth, endMonth] = this.monthlyRangeDates
                // Normalize inverted selection from the month range picker
                if (startMonth > endMonth) {
                    ;[startMonth, endMonth] = [endMonth, startMonth]
                }

                const today = new Date()
                const currentMonth = today.toISOString().substr(0, 7)
                if (endMonth > currentMonth) {
                    endMonth = currentMonth
                }

                // Cap at 3 months inclusive (start + 2 months)
                const startDate = moment(startMonth + '-01')
                const maxEnd = startDate
                    .clone()
                    .add(2, 'months')
                    .format('YYYY-MM')
                if (endMonth > maxEnd) {
                    endMonth = maxEnd > currentMonth ? currentMonth : maxEnd
                }

                if (
                    startMonth !== this.monthlyRangeDates[0] ||
                    endMonth !== this.monthlyRangeDates[1]
                ) {
                    this.monthlyRangeDates = [startMonth, endMonth]
                }
            }
        },
        validateMaxMonthlyRange() {
            if (!this.monthlyRangeDates || this.monthlyRangeDates.length !== 2)
                return true
            let startMonth = this.monthlyRangeDates[0]
            let endMonth = this.monthlyRangeDates[1]
            if (!startMonth || !endMonth) return true
            if (startMonth > endMonth) {
                ;[startMonth, endMonth] = [endMonth, startMonth]
            }
            const today = new Date()
            const currentMonth = today.toISOString().substr(0, 7)

            if (endMonth > currentMonth)
                return 'End month cannot be in the future'

            const startDate = moment(startMonth + '-01')
            const maxEnd = startDate.clone().add(2, 'months')
            if (moment(endMonth + '-01').isAfter(maxEnd)) {
                return 'Monthly range cannot exceed 3 months'
            }
            return true
        },
        parseDate(dateString) {
            // Parse date in DD/MM/YYYY format using moment
            return moment(dateString, 'DD/MM/YYYY')
        },
        // Dialog
        closeWoReport() {
            this.$emit('closeWoReport')
        },
    },
}
</script>
<style scoped>
.wide-date-menu {
    min-width: 290px !important;
}
</style>
